/**
 * Adapter that exposes a uniform token API over two backends.
 *
 *   TOKEN_STORAGE = 'memory'  (default, recommended)
 *     Access token  → JS module closure.  XSS cannot steal it.
 *     Refresh token → localStorage.  Survives page refresh; only usable at
 *                     /api/auth/refresh and can be revoked server-side.
 *     On page load the app silently exchanges the stored refresh token for a
 *     fresh access token (see App.js rehydration effect).
 *
 *   TOKEN_STORAGE = 'localStorage'
 *     Both tokens in localStorage.  Original behaviour — no UX disruption on
 *     page refresh, but tokens are readable by any JS on the page.
 *
 * Switch by setting TOKEN_STORAGE in src/environments/*.js.
 */

import Constants from '../Utils/Constants';

// ── Shared localStorage helpers ───────────────────────────────────────────────
const LS_KEY = 'store';
const _lsRead  = ()      => JSON.parse(localStorage.getItem(LS_KEY)) || {};
const _lsPatch = (patch) => localStorage.setItem(LS_KEY, JSON.stringify({ ..._lsRead(), ...patch }));
const _lsDel   = (key)   => {
  const s = _lsRead();
  delete s[key];
  localStorage.setItem(LS_KEY, JSON.stringify(s));
};

// ── Memory backend ────────────────────────────────────────────────────────────
// Access token lives only in this closure.
// Refresh token is persisted to localStorage so page refresh doesn't force logout.
let _mem_access = null;

const memory = {
  getAccessToken:  () => _mem_access,

  // On page refresh _mem_access is null but localStorage may have a refresh token
  // from a prior session — fall through to localStorage so refresh() can rehydrate.
  getRefreshToken: () => _lsRead().refreshtoken || null,

  setTokens: (access, refresh) => {
    _mem_access = access ?? null;
    // Persist refresh token so it survives page refresh
    if (refresh !== undefined) _lsPatch({ refreshtoken: refresh ?? null });
  },

  clearTokens: () => {
    _mem_access = null;
    _lsDel('refreshtoken');
  },
};

// ── localStorage backend ──────────────────────────────────────────────────────
const ls = {
  getAccessToken:  () => _lsRead().token        || null,
  getRefreshToken: () => _lsRead().refreshtoken || null,
  setTokens: (access, refresh) => _lsPatch({
    token:        access  ?? null,
    refreshtoken: refresh ?? null,
  }),
  clearTokens: () => {
    _lsDel('token');
    _lsDel('refreshtoken');
  },
};

// ── Active backend ────────────────────────────────────────────────────────────
const backend = Constants.tokenStorage === 'memory' ? memory : ls;

export const getAccessToken  = ()                => backend.getAccessToken();
export const getRefreshToken = ()                => backend.getRefreshToken();
export const setTokens       = (access, refresh) => backend.setTokens(access, refresh);
export const clearTokens     = ()                => backend.clearTokens();
